import React, { useRef } from "react"
import { Animated, TextInput, View } from "react-native"
import { Formik } from "formik"
import { useAppSelector } from "../../store"
import * as yup from 'yup'
import { ThemedTextInput } from "../input/textinputs/themed-text-input"
import { GENDER_IDENTITIES, RACE_ETHNICITY_IDENTITIES, SEXUAL_ORIENTATION_IDENTITIES } from "@ecountabl/lib"
import { ThemedDropdown } from "../input/misc/themed-dropdown"

type Props = {
  onValidationSuccess: (handleSubmit: () => void) => void
  onValidationFailure: () => void

  onSubmit: (values: FormValues) => void

  loading?: boolean
  needsModification?: boolean
}

type FormValues = {
  race: string

  gender: string
  genderDescription: string
  transIdentity: string

  sexualOrientation: string
  sexualOrientationDescription: string

  disabledIdentity: string
}

const IdentityInfoForm: React.FC<Props> = ({
  onValidationSuccess,
  onValidationFailure,
  loading,
  onSubmit,
  needsModification,
}: Props): React.ReactElement => {
  const { user } = useAppSelector((state) => state.auth)

  const genderDescriptionRef = useRef<TextInput>(null)
  const sexualOrientationDescriptionRef = useRef<TextInput>(null)

  const descriptionFieldHeights = useRef<{
    genderDescription: Animated.Value
    sexualOrientationDescription: Animated.Value
  }>({
    genderDescription: new Animated.Value(
      user.identityInfo?.genderDescription ? 100 : 0
    ),
    sexualOrientationDescription: new Animated.Value(
      user.identityInfo?.sexualOrientationDescription ? 100 : 0
    ),
  })

  const beginAnimation = (ref: keyof typeof descriptionFieldHeights.current, to: 0 | 100) => {
      Animated.timing(descriptionFieldHeights.current[ref], {
        toValue: to,
        duration: 250,
        useNativeDriver: false,
      }).start()
  }

  return (
    <Formik<FormValues>
      initialValues={{
        race: user.identityInfo?.race || "",

        gender: user.identityInfo?.gender || "",
        genderDescription: user.identityInfo?.genderDescription || "",
        
        transIdentity: user.identityInfo?.transIdentity || "",

        sexualOrientation: user.identityInfo?.sexualOrientation || "",
        sexualOrientationDescription: user.identityInfo?.sexualOrientationDescription || "",

        disabledIdentity: user.identityInfo?.disabledIdentity || "",
      }}
      onSubmit={onSubmit}
      validationSchema={
        yup.object().shape({
          race: yup.string().oneOf(RACE_ETHNICITY_IDENTITIES, "Invalid race").label("Race"),

          gender: yup.string().optional().oneOf(GENDER_IDENTITIES, "Invalid gender").label("Gender"),
          genderDescription: yup.string()
            .when('gender', {
              is: (val: string) => val === "Other (Please Specify)",
              then: schema => schema.required("Please specify your gender identity").min(1, "Please specify your gender identity"),
              otherwise: schema => schema.optional()
            })
            .label("Gender Identity Description"),
          transIdentity: yup.string().optional().oneOf(["Yes", "No"], "Invalid transgender identity").label("Transgender Identity"),

          sexualOrientation: yup.string().optional().oneOf(SEXUAL_ORIENTATION_IDENTITIES, "Invalid sexual orientation").label("Sexual Orientation"),
          sexualOrientationDescription: yup.string()
            .when('sexualOrientation', {
              is: (val: string) => val === "Other (Please Specify)",
              then: schema => schema.required("Please specify your sexual orientation").min(1, "Please specify your sexual orientation"),
              otherwise: schema => schema.optional()
            })
            .label("Sexual Orientation Description"),

          disabledIdentity: yup.string().optional().oneOf(["Yes", "No"], "Invalid disabled identity").label("Disabled Identity"),
        })
      }
      validateOnMount
    >
      {({
        initialValues,
        handleChange,
        handleSubmit,
        values,
        errors,
        touched,
        isValid,
      }) => {
        const beenModified = Object.keys(values).some(key => values[key] !== initialValues[key])
        if (
          isValid &&
          (
            !needsModification ||
            beenModified
          )
        ) {
          onValidationSuccess?.(handleSubmit)
        } else {
          onValidationFailure?.()
        }

        return <>
          {/* Race / Ethnicity */}
          <ThemedDropdown
            label="Race / Ethnicity"
            data={RACE_ETHNICITY_IDENTITIES.map(key => ({ key }))}
            value={values.race}
            onChange={handleChange("race")}
            valueField="key"
            displayTextField="key"
            placeholder="Select One"
            disabled={loading}
          />
          {/* Gender Identity */}
          <View style={{gap: 10}}>
            <ThemedDropdown
              label="Gender"
              data={GENDER_IDENTITIES.map(key => ({ key }))}
              value={values.gender}
              onChange={(v) => {
                if (v === "Other (Please Specify)") {
                  beginAnimation("genderDescription", 100)
                } else {
                  beginAnimation("genderDescription", 0)
                }
                handleChange("gender")(v)
              }}
              valueField="key"
              displayTextField="key"
              placeholder="Select One"
              disabled={loading}
            />
            <Animated.View style={{maxHeight: descriptionFieldHeights.current.genderDescription, overflow: "hidden"}}>
              <ThemedTextInput
                label="Gender Identity Description"
                value={values.genderDescription}
                required={values.gender === "Other (Please Specify)"}
                onChange={handleChange("genderDescription")}
                errorText={
                  touched.genderDescription && errors.genderDescription
                    ? errors.genderDescription
                    : undefined
                }
                disabled={loading}
                placeholder="Describe your gender here"
                textInputProps={{
                  returnKeyType: "done",
                  onSubmitEditing: () => {
                    // Last input in form - blur to dismiss keyboard
                    genderDescriptionRef.current?.blur()
                  }
                }}
              />
            </Animated.View>
          </View>
          {/* Trans Identity */}
          <ThemedDropdown
            label="Do you identify as transgender?"
            data={[{key: "Yes"}, {key: "No"}]}
            value={values.transIdentity}
            onChange={handleChange("transIdentity")}
            valueField="key"
            displayTextField="key"
            placeholder="Select One"
            disabled={loading}
          />
          {/* Sexual Orientation */}
          <View style={{gap: 10}}>
            <ThemedDropdown
              label="Sexual Orientation"
              data={SEXUAL_ORIENTATION_IDENTITIES.map(key => ({ key }))}
              value={values.sexualOrientation}
              onChange={(v) => {
                if (v === "Other (Please Specify)") {
                  beginAnimation("sexualOrientationDescription", 100)
                } else {
                  beginAnimation("sexualOrientationDescription", 0)
                }
                handleChange("sexualOrientation")(v)
              }}
              valueField="key"
              displayTextField="key"
              placeholder="Select One"
              disabled={loading}
            />
            <Animated.View style={{maxHeight: descriptionFieldHeights.current.sexualOrientationDescription, overflow: "hidden"}}>
              <ThemedTextInput
                label="Sexual Orientation Description"
                value={values.sexualOrientationDescription}
                onChange={handleChange("sexualOrientationDescription")}
                required={values.sexualOrientation === "Other (Please Specify)"}
                errorText={
                  touched.sexualOrientationDescription && errors.sexualOrientationDescription
                    ? errors.sexualOrientationDescription
                    : undefined
                }
                disabled={loading}
                placeholder="Describe your sexual orientation here"
                textInputProps={{
                  returnKeyType: "done",
                  onSubmitEditing: () => {
                    // Last input in form - blur to dismiss keyboard
                    sexualOrientationDescriptionRef.current?.blur()
                  }
                }}
              />
            </Animated.View>
          </View>
          {/* Disabled Identity */}
          <ThemedDropdown
            label="Do you identify as disabled?"
            data={[{key: "Yes"}, {key: "No"}]}
            value={values.disabledIdentity}
            onChange={handleChange("disabledIdentity")}
            valueField="key"
            displayTextField="key"
            placeholder="Select One"
            disabled={loading}
          />
        </>
      }}
    </Formik>
  )
}

export default IdentityInfoForm